import { useState, type FormEvent } from "react";
import { ApiError, api, type User } from "../api";
import { Credit } from "../Credit";
import { Icon } from "../icons";
import { Logo } from "../Logo";

export function LoginPage({ needsSetup, onSuccess }: { needsSetup: boolean; onSuccess: (user: User) => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setPending(true);
    try {
      const path = needsSetup ? "/api/auth/setup" : "/api/auth/login";
      const body = needsSetup ? { name, email, password, setupToken } : { email, password };
      const result = await api<{ user: User }>(path, { method: "POST", body: JSON.stringify(body) });
      onSuccess(result.user);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Connexion impossible");
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="login">
      <section className="login-visual">
        <div>
          <p className="kicker">Anytime Fitness Trois-Rivières</p>
          <h1>Les télés du club, au même endroit.</h1>
          <ul className="login-points">
            <li>
              <span className="bubble"><Icon name="media" /></span>
              <span><strong>Médias</strong>Vidéos et images envoyées une fois.</span>
            </li>
            <li>
              <span className="bubble"><Icon name="list" /></span>
              <span><strong>Playlists</strong>L'ordre de passage sur chaque télé.</span>
            </li>
            <li>
              <span className="bubble"><Icon name="shield" /></span>
              <span><strong>Autorisation</strong>Un code à 6 chiffres avant la première image.</span>
            </li>
          </ul>
        </div>
        <Credit tone="paper" />
      </section>
      <section className="login-panel">
        <form className="login-card" onSubmit={submit}>
          <Logo />
          <h2>{needsSetup ? "Créer l'administrateur" : "Connexion"}</h2>
          <p className="muted">
            {needsSetup
              ? "Le premier compte gère les télés, les playlists et les médias."
              : "Réservé à l'équipe du club. Les téléviseurs passent par un code, pas par ce mot de passe."}
          </p>
          {needsSetup ? (
            <label>
              Nom
              <input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" required minLength={2} />
            </label>
          ) : null}
          <label>
            E-mail
            <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="username" required />
          </label>
          <label>
            Mot de passe
            {needsSetup ? <span className="field-hint">10 caractères minimum.</span> : null}
            <span className="password-field">
              <input
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type={showPassword ? "text" : "password"}
                autoComplete={needsSetup ? "new-password" : "current-password"}
                required
                minLength={10}
              />
              <button
                type="button"
                className="icon-button"
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                onClick={() => setShowPassword((current) => !current)}
              >
                <Icon name={showPassword ? "eyeOff" : "eye"} />
              </button>
            </span>
          </label>
          {needsSetup ? (
            <label>
              Jeton d'installation
              <span className="field-hint">Demandé une seule fois, pour le premier compte.</span>
              <input value={setupToken} onChange={(event) => setSetupToken(event.target.value)} type="password" autoComplete="off" required minLength={16} />
            </label>
          ) : null}
          {error ? <p className="banner error" role="alert">{error}</p> : null}
          <button className="primary" type="submit" disabled={pending}>
            {pending ? "Vérification…" : needsSetup ? "Créer le compte" : "Entrer"}
          </button>
        </form>
        <Credit />
      </section>
    </main>
  );
}
